import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUnleashContext } from "@unleash/proxy-client-react";
import { useQueryClient } from "@tanstack/react-query";
import useUsers from "./useUsers";
import { refreshUserContext, getTenantIdFromToken } from "@/utils/auth";
import { useTheme } from "./useTheme";
import { clearDashboardSkeletonCache } from "@/lib/dashboard-skeleton-cache";
import { logger } from "@/lib/logger";

const LAST_TENANT_ID_KEY = "last-tenant-id";

export const useOrganizationSwitch = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { onLogin, useQueryMe } = useUsers();
  const updateContext = useUnleashContext();
  const { refreshTenantStylesheet } = useTheme();
  const queryClient = useQueryClient();
  const me = useQueryMe(true);
  const previousTenantIdRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !me.data || isCheckingRef.current) return;

    const checkOrganizationChange = async () => {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;

      try {
        const currentTenantId = await getTenantIdFromToken(
          getAccessTokenSilently
        );

        // Check if tenant ID changed by comparing with localStorage
        const lastTenantId = localStorage.getItem(LAST_TENANT_ID_KEY);
        const isOrgSwitch = lastTenantId && lastTenantId !== currentTenantId;

        // On first load
        if (!isInitializedRef.current) {
          if (isOrgSwitch) {
            // Refresh token first so dashboard persist uses the new tenant; then clear skeleton
            // cache so we never leave an orphan document scoped to the previous org.
            await refreshUserContext(
              getAccessTokenSilently,
              onLogin,
              updateContext,
              me.data,
              true // Reset login flag on organization switch
            );
            clearDashboardSkeletonCache();
            await refreshTenantStylesheet();
            queryClient.invalidateQueries();
          } else {
            // Normal page refresh - don't reset login flag
            await refreshUserContext(
              getAccessTokenSilently,
              onLogin,
              updateContext,
              me.data,
              false // Don't reset login flag on normal page refresh
            );
          }

          // Store the current tenant ID for future comparisons
          if (currentTenantId) {
            localStorage.setItem(LAST_TENANT_ID_KEY, currentTenantId);
          }

          previousTenantIdRef.current = currentTenantId || null;
          isInitializedRef.current = true;
          isCheckingRef.current = false;
          return;
        }

        // Runtime organization change (after initial load, while app is running)
        if (previousTenantIdRef.current !== currentTenantId) {
          await refreshUserContext(
            getAccessTokenSilently,
            onLogin,
            updateContext,
            me.data,
            true // Reset login flag on organization switch
          );
          clearDashboardSkeletonCache();
          await refreshTenantStylesheet();
          queryClient.invalidateQueries();

          // Store the new tenant ID
          if (currentTenantId) {
            localStorage.setItem(LAST_TENANT_ID_KEY, currentTenantId);
          }

          previousTenantIdRef.current = currentTenantId || null;
        }
      } catch (error) {
        logger.error("Failed to check for an organization change", {
          operation: "checkOrganizationChange",
          error,
        });
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkOrganizationChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    me.data,
    getAccessTokenSilently,
    onLogin,
    updateContext,
    refreshTenantStylesheet,
  ]);

  return {
    refreshContext: async () => {
      if (me.data) {
        await refreshUserContext(
          getAccessTokenSilently,
          onLogin,
          updateContext,
          me.data,
          true // Reset login flag on manual refresh
        );

        // Update stored tenant ID
        const currentTenantId = await getTenantIdFromToken(
          getAccessTokenSilently
        );
        if (currentTenantId) {
          localStorage.setItem(LAST_TENANT_ID_KEY, currentTenantId);
        }
      }
    },
  };
};
